import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { RECEIPT_COLORS } from "@/shared/utils/paymentReceipt/receiptBrand";

import type { PayinData } from "../types/payins.types";
import { drawPayinPdfLogo, preparePayinPdfFonts } from "./payinPdfBrand";

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

const formatCurrency = (amount: number, currency = "COP") =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);

const formatDate = (date?: string) => {
  if (!date) return "No informado";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleString("es-CO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Función para normalizar y formatear el estado (primera letra mayúscula, resto minúsculas)
const formatStatus = (status: string): string => {
  if (!status) return "";
  const normalized = status.toLowerCase().trim();
  if (normalized.length === 0) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const safe = (value?: string | number) =>
  value === undefined || value === null || value === "" ? "No informado" : String(value);

export const generateColombiaPayinPdf = async (payin: PayinData) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const containerWidth = pageWidth * 0.72;
  const containerX = (pageWidth - containerWidth) / 2;
  const innerPadding = 24;
  const innerX = containerX + innerPadding;
  const contentWidth = containerWidth - innerPadding * 2;
  const startY = 96;
  let cursorY = startY;

  // The Coloca 2026 document palette, shared with the payout comprobante and with the backend's
  // transactional emails. The slate values that used to sit here (`#0f172a`, `#475569`, `#f8fafc`,
  // `#e2e8f0`, `#f1f5f9`, `#cbd5e1`) belonged to no Coloca surface at all.
  const brandColor = RECEIPT_COLORS.ink;
  const mutedColor = RECEIPT_COLORS.muted;
  const fonts = await preparePayinPdfFonts(doc);

  const statusLabel = formatStatus(payin.status || "Pending");

  // Logo — the current wordmark, rasterized from the SVG the app renders, at its real aspect ratio.
  const logoWidth = 176;
  await drawPayinPdfLogo(doc, containerX + containerWidth - logoWidth - 12, cursorY - 24, logoWidth);

  // Encabezado
  doc.setFont(fonts.sans, "bold");
  doc.setFontSize(20);
  doc.setTextColor(brandColor);
  doc.text("Comprobante", innerX, cursorY);
  cursorY += 36;

  // Datos del pagador (sube a la primera sección)
  doc.setFont(fonts.sans, "bold");
  doc.setFontSize(13);
  doc.setTextColor(brandColor);
  doc.text("Pagador", innerX, cursorY);
  doc.setFont(fonts.sans, "normal");
  doc.setFontSize(10);
  doc.setTextColor(mutedColor);
  doc.text(
    [
      `Nombre: ${safe(payin.payer_name)}`,
      `Documento: ${safe(payin.payer_document_type)} ${safe(payin.payer_document)}`,
      `Email: ${safe(payin.payer_email)}`,
      `Teléfono: ${safe(payin.payer_phone_number)}`,
    ],
    innerX,
    cursorY + 18,
    { lineHeightFactor: 1.5 },
  );
  cursorY += 90;

  // Resumen de pago (debajo de pagador)
  const summaryHeight = 64;
  doc.setFillColor(RECEIPT_COLORS.surface);
  doc.roundedRect(innerX, cursorY, contentWidth, summaryHeight, 10, 10, "F");

  // Monto a pagar (izquierda)
  doc.setFontSize(12);
  doc.setTextColor(mutedColor);
  doc.text("Monto a pagar", innerX + 16, cursorY + 18);
  doc.setFont(fonts.mono, "bold");
  doc.setFontSize(22);
  doc.setTextColor(brandColor);
  doc.text(formatCurrency(payin.amount, "COP"), innerX + 16, cursorY + 40);

  // Monto a recibir (derecha, solo si existe)
  if (typeof payin.amount_received === "number") {
    const rightX = innerX + contentWidth / 2 + 12;
    doc.setFont(fonts.sans, "normal");
    doc.setFontSize(12);
    doc.setTextColor(mutedColor);
    doc.text("Monto a recibir", rightX, cursorY + 18);
    doc.setFont(fonts.mono, "bold");
    doc.setFontSize(22);
    doc.setTextColor(brandColor);
    doc.text(formatCurrency(payin.amount_received, "COP"), rightX, cursorY + 40);
  }

  cursorY += summaryHeight + 20;

  // Tabla de detalles principales
  autoTable(doc, {
    startY: cursorY,
    head: [["Detalle", "Valor"]],
    body: [
      ["Estado", statusLabel],
      ["Método", safe(payin.payment_method).toUpperCase()],
      ["Creado", formatDate(payin.created_at)],
    ],
    theme: "grid",
    styles: {
      font: fonts.sans,
      fontSize: 10,
      cellPadding: 8,
      lineColor: RECEIPT_COLORS.line,
      textColor: brandColor,
    },
    headStyles: {
      font: fonts.sans,
      fillColor: RECEIPT_COLORS.surface,
      textColor: RECEIPT_COLORS.brand,
      fontStyle: "bold",
    },
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.row.index === 0) {
        hookData.cell.styles.fontStyle = "bold";
      }
    },
    tableWidth: contentWidth,
    margin: { left: innerX },
  });
  cursorY = ((doc as JsPdfWithAutoTable).lastAutoTable?.finalY || cursorY) + 18;

  // Nota final
  doc.setTextColor(mutedColor);
  doc.setFont(fonts.sans, "normal");
  doc.setFontSize(9);
  const footerNote = doc.splitTextToSize(
    "Este comprobante fue generado electrónicamente. Conserva este PDF como respaldo de la operación.",
    contentWidth,
  );
  const footerY = Math.max(cursorY + 24, startY + 60);
  doc.text(footerNote, innerX, footerY);
  cursorY = footerY + 12 * footerNote.length;

  // Marco del contenedor
  const containerHeight = Math.min(Math.max(cursorY - startY + 40, 320), pageHeight * 0.7);
  doc.setDrawColor(RECEIPT_COLORS.line);
  doc.setLineWidth(1);
  doc.roundedRect(containerX, startY - 30, containerWidth, containerHeight, 12, 12);

  doc.save(`payin-col-${payin.id}.pdf`);
};
